import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anonymous, object = user
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const register = async (email, password, name) => {
    setError("");
    try {
      // Attribute conversion to A/B banner variant the user saw on landing.
      const promo_variant = (() => {
        try { return window.localStorage.getItem("hot.variant") || undefined; } catch { return undefined; }
      })();
      const { data } = await api.post("/auth/register", { email, password, name, promo_variant });
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
  };

  const toggleFavorite = async (teamId) => {
    if (!user) return;
    const isFav = (user.favorites || []).includes(teamId);
    try {
      const { data } = isFav
        ? await api.delete(`/favorites/${teamId}`)
        : await api.post(`/favorites/${teamId}`);
      setUser({ ...user, favorites: data.favorites });
    } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, error, login, register, logout, refresh, toggleFavorite }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
